import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiSelect } from "@/components/kit/MultiSelect";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtNumber } from "@/lib/format";

const EMPTY = { date_from: "", date_to: "", offer_ids: [], stream_ids: [], buyer_ids: [], advertiser_ids: [], countries: [], status: "" };

function Row({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

export function CleanLogsDialog({ open, onOpenChange, offerOptions, streamOptions, buyerOptions, advertiserOptions, currentFilters }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [fMode, setFMode] = useState(false);
  const [fAll, setFAll] = useState(true);
  const [fRange, setFRange] = useState({ from: "", to: "" });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { if (!open) { setPreview(null); setJob(null); setF(EMPTY); setFMode(false); setFAll(true); setFRange({ from: "", to: "" }); } }, [open]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPreview(null); };

  const useCurrent = () => {
    const flt = currentFilters?.filters || {};
    const c = currentFilters?.custom || {};
    const arr = (v) => (Array.isArray(v) ? v : v && v !== "all" ? [v] : []);
    setF({
      date_from: c.dateFrom || "", date_to: c.dateTo || "",
      offer_ids: arr(flt.offer_id), stream_ids: arr(flt.stream_id), buyer_ids: arr(flt.buyer_id),
      advertiser_ids: [], countries: arr(flt.country),
      status: !Array.isArray(flt.status) && flt.status && flt.status !== "all" ? flt.status : (Array.isArray(flt.status) && flt.status.length === 1 ? flt.status[0] : ""),
    });
    setPreview(null);
    toast.success("Current page filters applied");
  };

  const effFilters = () => (fMode
    ? { filtered_only: true, ...(fAll ? {} : { date_from: fRange.from, date_to: fRange.to }) }
    : f);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await http.post("/clicks/clean-logs", { mode: "preview", filters: effFilters() });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const start = async (mode) => {
    setConfirmAll(false);
    try {
      const { data } = await http.post("/clicks/clean-logs", { mode, filters: effFilters() });
      setJob(data);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await http.get(`/clicks/cleanup-jobs/${data.id}`);
          setJob(j);
          if (j.status !== "running") {
            clearInterval(pollRef.current);
            qc.invalidateQueries({ queryKey: ["clicks-server"] });
            qc.invalidateQueries({ queryKey: ["conversions"] });
            if (j.status === "done") toast.success(`Cleanup finished: ${fmtNumber(j.deleted_clicks)} clicks${j.deleted_conversions ? ` + ${fmtNumber(j.deleted_conversions)} conversions` : ""} removed`);
            else toast.error(`Cleanup failed: ${j.error || "unknown error"}`);
          }
        } catch { /* keep polling */ }
      }, 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start cleanup"); }
  };

  const running = job && job.status === "running";
  const pct = job && job.total_clicks ? Math.min(100, Math.round((job.processed / job.total_clicks) * 100)) : 0;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!running) onOpenChange(o); }}>
        <DialogContent className="max-w-xl border-white/[0.1] bg-[#141417]" data-testid="clean-logs-dialog">
          <DialogHeader>
            <DialogTitle>Clean click logs</DialogTitle>
            <DialogDescription>Mass-delete clicks in your tenant by filters. Preview first, then choose how to handle linked conversions.</DialogDescription>
          </DialogHeader>
          {job ? (
            <div className="space-y-3 py-2" data-testid="clean-logs-progress">
              <p className="text-sm text-foreground">{running ? "Cleanup in progress…" : job.status === "done" ? "Cleanup finished" : "Cleanup failed"}</p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.08]">
                <div className={`h-full rounded-full transition-all ${job.status === "failed" ? "bg-[#EF4444]" : "bg-[#22C55E]"}`} style={{ width: `${job.status === "running" ? pct : 100}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">
                Processed {fmtNumber(job.processed)} / {fmtNumber(job.total_clicks)} clicks · Deleted {fmtNumber(job.deleted_clicks)} clicks{job.deleted_conversions ? ` + ${fmtNumber(job.deleted_conversions)} conversions` : ""}
              </p>
              {job.status === "failed" && <p className="text-xs text-[#EF4444]">{job.error}</p>}
              {!running && (
                <div className="flex justify-end">
                  <Button onClick={() => onOpenChange(false)} data-testid="clean-logs-close" className="h-9 bg-white text-black hover:bg-white/90">Close</Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {!fMode ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                <Row label="Period">
                  <DateRangePicker from={f.date_from} to={f.date_to}
                    onChange={({ from, to }) => { setF((s) => ({ ...s, date_from: from, date_to: to })); setPreview(null); }}
                    placeholder="Any period" testId="clean-date-range" />
                </Row>
                <Row label="Unique status">
                  <Select value={f.status || "all"} onValueChange={(v) => set("status", v === "all" ? "" : v)}>
                    <SelectTrigger className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm" data-testid="clean-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                      <SelectItem value="all">All clicks</SelectItem>
                      <SelectItem value="unique">Unique only</SelectItem>
                      <SelectItem value="duplicate">Non-unique only</SelectItem>
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Offers"><MultiSelect values={f.offer_ids} onChange={(v) => set("offer_ids", v)} options={offerOptions} label="All offers" searchPlaceholder="Search offers..." testId="clean-offers" widthClass="w-[300px]" triggerClass="h-9 rounded-lg" /></Row>
                <Row label="Streams"><MultiSelect values={f.stream_ids} onChange={(v) => set("stream_ids", v)} options={streamOptions} label="All streams" searchPlaceholder="Search streams..." testId="clean-streams" widthClass="w-[300px]" triggerClass="h-9 rounded-lg" /></Row>
                <Row label="Advertisers"><MultiSelect values={f.advertiser_ids} onChange={(v) => set("advertiser_ids", v)} options={advertiserOptions} label="All advertisers" searchPlaceholder="Search advertisers..." testId="clean-advertisers" widthClass="w-[300px]" triggerClass="h-9 rounded-lg" /></Row>
                <Row label="Buyers"><MultiSelect values={f.buyer_ids} onChange={(v) => set("buyer_ids", v)} options={buyerOptions} label="All buyers" searchPlaceholder="Search buyers..." testId="clean-buyers" widthClass="w-[300px]" triggerClass="h-9 rounded-lg" /></Row>
                <Row label="Countries"><MultiSelect values={f.countries} onChange={(v) => set("countries", v)} options={[]} country label="All countries" searchPlaceholder="Search countries..." testId="clean-countries" widthClass="w-[300px]" triggerClass="h-9 rounded-lg" /></Row>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={useCurrent} data-testid="clean-use-current" className="h-9 border-white/[0.15] bg-transparent text-xs">Use current page filters</Button>
                <Button onClick={runPreview} disabled={previewing} data-testid="clean-preview-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90 text-xs">
                  {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Preview
                </Button>
                <Button variant="outline" onClick={() => { setFMode(true); setPreview(null); }} data-testid="clean-filtered-btn"
                  className="ml-auto h-9 gap-1.5 border-[#EAB308]/40 bg-[#EAB308]/[0.08] text-xs text-[#EAB308] hover:bg-[#EAB308]/15">
                  <Filter className="h-3.5 w-3.5" /> Clean Filtered
                </Button>
              </div>
                </>
              ) : (
                <div className="space-y-3 rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.05] px-3 py-3" data-testid="clean-filtered-panel">
                  <div>
                    <p className="text-sm font-medium text-[#EAB308]">Filtered clicks only</p>
                    <p className="text-[11px] text-muted-foreground">Removes only clicks with the yellow “Filtered” mark (traffic-filter hits). Unique / Duplicate clicks are never affected.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant={fAll ? "default" : "outline"} onClick={() => { setFAll(true); setPreview(null); }} data-testid="clean-filtered-all"
                      className={`h-8 text-xs ${fAll ? "bg-white text-black hover:bg-white/90" : "border-white/[0.15] bg-transparent"}`}>Delete all filtered clicks</Button>
                    <Button variant={!fAll ? "default" : "outline"} onClick={() => { setFAll(false); setPreview(null); }} data-testid="clean-filtered-period"
                      className={`h-8 text-xs ${!fAll ? "bg-white text-black hover:bg-white/90" : "border-white/[0.15] bg-transparent"}`}>For selected period</Button>
                    {!fAll && (
                      <DateRangePicker from={fRange.from} to={fRange.to}
                        onChange={({ from, to }) => { setFRange({ from, to }); setPreview(null); }}
                        placeholder="Pick period" testId="clean-filtered-range" />
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => { setFMode(false); setPreview(null); }} data-testid="clean-filtered-back" className="h-9 border-white/[0.15] bg-transparent text-xs">Back</Button>
                    <Button onClick={runPreview} disabled={previewing || (!fAll && !fRange.from && !fRange.to)} data-testid="clean-filtered-preview" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90 text-xs">
                      {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Preview
                    </Button>
                  </div>
                </div>
              )}
              {preview && (
                <div className="space-y-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-3" data-testid="clean-preview-result">
                  <p className="text-sm text-foreground">
                    Found <span className="font-semibold">{fmtNumber(preview.clicks)}</span> clicks — <span className="text-[#22C55E]">{fmtNumber(preview.without_conversions)}</span> without conversions, <span className="text-[#F59E0B]">{fmtNumber(preview.with_conversions)}</span> with <span className="font-semibold">{fmtNumber(preview.conversions)}</span> linked conversions.
                  </p>
                  {preview.clicks === 0 ? (
                    <p className="text-xs text-muted-foreground">Nothing to delete with these filters.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" disabled={preview.without_conversions === 0} onClick={() => start("clicks_only")}
                        data-testid="clean-clicks-only" className="h-9 border-white/[0.15] bg-transparent text-xs">
                        Delete only clicks without conversions ({fmtNumber(preview.without_conversions)})
                      </Button>
                      <Button onClick={() => setConfirmAll(true)} data-testid="clean-everything"
                        className="h-9 bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]">
                        Delete everything (including conversions)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="clean-final-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete everything?</AlertDialogTitle>
            <AlertDialogDescription>
              All {fmtNumber(preview?.clicks || 0)} matched clicks and all {fmtNumber(preview?.conversions || 0)} linked conversions (with their postback and Meta logs) will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clean-final-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="clean-final-confirm" onClick={() => start("everything")}
              className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
